import { PaginationResponse } from "../../Shared/Pagination/PaginationResponse";

export interface SmartAnalyticsCandidateRow {
  userId: string;
  fullName: string;
  email: string;
  userStatus: string | null;
  availabilityStatus: string | null;
  city: string | null;
  currentRole: string | null;
  currentCompany: string | null;
  totalYearsOfExperience: number | null;
  roleLevel: string | null;
  professionalHeadline: string | null;
  overallScore: number | null;
  /** 1/0 from a MySQL bit column projected as a number. */
  profileComplete: number;
  profileCompleteScore: number | null;
  profilePhotoUrl: string | null;
  phoneNumber: string | null;
  workEmail: string | null;
  linkedinVerified: number;
  workEmailVerified: number;
  phoneVerified: number;
  profileVerified: number;
  leadershipScore: number | null;
  architectureScore: number | null;
  businessImpactScore: number | null;
  achievementScore: number | null;
  decisionSpeed: string | null;
  noticePeriodDays: number | null;
  targetRoles: string | null;
  coreExpertise: string | null;
  notableCompanies: string | null;
  receivedInvitationCount: number;
  acceptedInvitationCount: number;
  rejectedInvitationCount: number;
  createdAt: string | null;
  updatedAt: string | null;
  lastActivityAt: string | null;
}

export interface SmartAnalyticsCandidateFilters {
  search?: string;
  status?: string;
  availabilityStatus?: string;
  profileComplete?: string;
  minScore?: number;
  maxScore?: number;
  minExperience?: number;
  maxExperience?: number;
  recommendation?: string;
}

export interface SmartAnalyticsCandidateActivityRow {
  activityId: string;
  eventType: string | null;
  category: string | null;
  message: string | null;
  actorUserId: string | null;
  actorName: string | null;
  actorRole: string | null;
  entityType: string | null;
  entityId: string | null;
  metadata: string | null;
  createdAt: string | null;
}

export interface SmartAnalyticsCandidateInvitationRow {
  invitationId: string;
  recruiterId: string | null;
  recruiterName: string | null;
  recruiterEmail: string | null;
  companyId: string | null;
  companyName: string | null;
  jobTemplateId: string | null;
  jobTitle: string | null;
  status: string | null;
  message: string | null;
  invitedAt: string | null;
  respondedAt: string | null;
  responseTimeInSeconds: number | null;
  createdAt: string | null;
}

export interface SmartAnalyticsCompanyRow {
  companyId: string;
  companyName: string;
  companyDomain: string | null;
  logoUrl: string | null;
  industry: string | null;
  companySizeRange: string | null;
  adminName: string | null;
  adminEmail: string | null;
  totalCredits: number;
  planName: string | null;
  planStatus: string | null;
  planExpireDate: string | null;
  planActive: number;
  trialUsed: number;
  createdAt: string | null;
  recruiterCount: number;
  activeRecruiterCount: number;
  jobTemplateCount: number;
  invitationCount: number;
  acceptedInvitationCount: number;
  paymentCount: number;
  totalRevenue: number;
  activityCount: number;
  feedbackCount: number;
  lastActivityAt: string | null;
}

export interface SmartAnalyticsActiveService {
  serviceType: string;
  planName: string | null;
  amount: number | null;
  creditsPurchased: number | null;
  active: boolean;
  expired: boolean;
  expireDate: string | null;
  daysUntilExpiry: number | null;
  paymentId: string | null;
  purchasedAt: string | null;
  connectionId: string | null;
}

export interface SmartAnalyticsCompanyOverview {
  companyId: string;
  companyName: string;
  companyDomain: string | null;
  logoUrl: string | null;
  industry: string | null;
  companySizeRange: string | null;
  website: string | null;
  headquartersLocation: string | null;
  totalCredits: number | null;
  trialUsed: boolean;
  createdAt: string | null;
  adminName: string | null;
  adminEmail: string | null;
  activeServices: SmartAnalyticsActiveService[];
  recruiterCount: number;
  activeRecruiterCount: number;
  jobTemplateCount: number;
  activeJobTemplateCount: number;
  invitationCount: number;
  pendingInvitationCount: number;
  acceptedInvitationCount: number;
  rejectedInvitationCount: number;
  paymentCount: number;
  totalRevenue: number;
  totalCreditsPurchased: number;
  activityCount: number;
  savedSearchCount: number;
  dataServiceQueryCount: number;
  feedbackCount: number;
  openFeedbackCount: number;
  lastActivityAt: string | null;
}

export interface SmartAnalyticsRecruiterRow {
  userId: string;
  fullName: string;
  email: string | null;
  designation: string | null;
  companyAdmin: number;
  recruiterActive: number;
  userStatus: string | null;
  joinedAt: string | null;
  invitationCount: number;
  jobTemplateCount: number;
  lastActivityAt: string | null;
}

export interface SmartAnalyticsPaymentRow {
  paymentId: string;
  planName: string | null;
  addon: number;
  dataService: number;
  amount: number | null;
  creditsPurchased: number | null;
  paymentStatus: string | null;
  completedAt: string | null;
  gateway: string | null;
  invoiceNumber: string | null;
  recruiterId: string | null;
  recruiterName: string | null;
  createdAt: string | null;
}

export interface SmartAnalyticsCompanyPayments {
  activeServices: SmartAnalyticsActiveService[];
  payments: PaginationResponse<SmartAnalyticsPaymentRow>;
}

export interface SmartAnalyticsJobTemplateRow {
  jobTemplateId: string;
  title: string | null;
  roleLevel: string | null;
  workType: string | null;
  active: number;
  recruiterId: string | null;
  postedBy: string | null;
  minExperience: string | null;
  maxExperience: string | null;
  compensationOffer: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SmartAnalyticsActivityRow {
  activityId: string;
  eventType: string | null;
  category: string | null;
  message: string | null;
  actorUserId: string | null;
  actorName: string | null;
  actorRole: string | null;
  entityType: string | null;
  candidateUserId: string | null;
  createdAt: string | null;
}

export interface SmartAnalyticsSearchRow {
  searchId: string;
  name: string;
  defaultFilter: number;
  alertEnabled: number;
  alertFrequency: string | null;
  userId: string;
  recruiterName: string | null;
  recruiterEmail: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SmartAnalyticsInvitationRow {
  invitationId: string;
  recruiterId: string | null;
  recruiterName: string | null;
  candidateId: string | null;
  candidateName: string | null;
  candidateEmail: string | null;
  jobTitle: string | null;
  status: string | null;
  message: string | null;
  invitedAt: string | null;
  respondedAt: string | null;
}

export interface SmartAnalyticsDataServiceRow {
  historyId: string;
  userId: string;
  recruiterName: string | null;
  recruiterEmail: string | null;
  connectionId: string | null;
  tableName: string | null;
  origin: string | null;
  rowCount: number | null;
  durationMs: number | null;
  success: number;
  executedSql: string | null;
  createdAt: string | null;
}

export interface SmartAnalyticsFeedbackRow {
  feedbackId: string;
  subject: string | null;
  message: string | null;
  category: string | null;
  status: string | null;
  priority: string | null;
  userId: string;
  recruiterName: string | null;
  email: string | null;
  userRole: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export const USER_STATUS_OPTIONS = [
  "PENDING",
  "UNDER_REVIEW",
  "EARLY_ACCESS_PENDING",
  "ACTIVE",
  "REJECTED",
] as const;

export const INVITATION_STATUS_OPTIONS = ["PENDING", "ACCEPTED", "REJECTED"] as const;

export const FEEDBACK_STATUS_OPTIONS = [
  "OPEN",
  "IN_PROGRESS",
  "RESOLVED",
  "CLOSED",
  "REOPEN",
] as const;

export const CANDIDATE_AVAILABILITY_OPTIONS = [
  "ACTUALLY_EXPLORING",
  "OPEN_TO_SELECTIVE_OFFERS",
  "NOT_ACTIVELY_LOOKING",
] as const;

export const PROFILE_COMPLETE_OPTIONS = [
  { value: "", label: "All profiles" },
  { value: "TRUE", label: "Complete" },
  { value: "FALSE", label: "Incomplete" },
] as const;

export const CANDIDATE_RECOMMENDATION_OPTIONS = [
  { value: "", label: "All candidates" },
  { value: "NO_INVITATIONS", label: "No invitations" },
  { value: "LOW_PROFILE_SCORE", label: "Low profile score" },
  { value: "NO_RECENT_ACTIVITY", label: "No recent activity" },
  { value: "INCOMPLETE_PROFILE", label: "Incomplete profile" },
] as const;

export const COMPANY_PLAN_STATUS_OPTIONS = [
  { value: "", label: "All plans" },
  { value: "ACTIVE", label: "Active plan" },
  { value: "EXPIRED", label: "Expired plan" },
  { value: "NO_PLAN", label: "No plan" },
  { value: "TRIAL", label: "Trial used" },
] as const;

export const COMPANY_BOOLEAN_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "TRUE", label: "Yes" },
  { value: "FALSE", label: "No" },
] as const;

export interface SmartAnalyticsCompanyFilters {
  search?: string;
  planStatus?: string;
  hasRecruiters?: string;
  hasPayments?: string;
}
